import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@sc/App/Components/XngCarousel";
import { CustomAppState } from "@sc/App/Store/Reducers";
import React, { useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    brands = "brands",
    numberOfBrandsForDesktop = "numberOfBrandsForDesktop",
    numberOfBrandsForTablet = "numberOfBrandsForTablet",
    numberOfBrandsForMobile = "numberOfBrandsForMobile",
    autoPlay = "autoPlay",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.brands]: {
            fields: {
                altText: string;
                imgSrc: string;
                link?: LinkFieldValue;
            };
        }[];
        [fields.numberOfBrandsForDesktop]: number;
        [fields.numberOfBrandsForTablet]: number;
        [fields.numberOfBrandsForMobile]: number;
        [fields.autoPlay]: boolean;
    };
}

type Props = OwnProps & HasShellContext;

export interface BrandCarouselHomePageStyles {
    container?: InjectableCss;
    img?: LazyImageProps;
    carouselExtendedStyles?: SwiperStyle;
}

export const brandCarouselHomePageStyles: BrandCarouselHomePageStyles = {};

const styles = brandCarouselHomePageStyles;

const BrandCarouselHomePage = ({
    fields: { brands, numberOfBrandsForDesktop, numberOfBrandsForTablet, numberOfBrandsForMobile, autoPlay },
}: Props) => {
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => {
        return {
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
            isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        };
    });
    // XNG-Changes
    const loopedBrands = useMemo(() => {
        const numberOfBrandsForCurrentScreen = isTabletScreen
            ? numberOfBrandsForTablet
            : isMobileScreen
            ? numberOfBrandsForMobile
            : numberOfBrandsForDesktop;
        if (brands.length > numberOfBrandsForCurrentScreen && brands.length < 2 * numberOfBrandsForCurrentScreen) {
            return [...brands, ...brands];
        }
        return brands;
    }, [brands, isTabletScreen, isMobileScreen]);

    return (
        <XngCarousel
            breakpoints={{
                320: {
                    slidesPerView: +numberOfBrandsForMobile,
                    spaceBetween: 10,
                },
                769: {
                    slidesPerView: +numberOfBrandsForTablet,
                    spaceBetween: 10,
                },
                1025: {
                    slidesPerView: +numberOfBrandsForDesktop,
                    spaceBetween: 10,
                },
            }}
            extendedStyles={styles.carouselExtendedStyles}
            {...{ autoPlay }}
            loop={autoPlay}
            scrollbar={!autoPlay}
            navigation={!autoPlay}
            speed={autoPlay ? 3500 : 0}
            autoplay={
                autoPlay
                    ? {
                          delay: 1,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                          stopOnLastSlide: false,
                      }
                    : false
            }
            freeMode={autoPlay}
        >
            {(autoPlay ? loopedBrands : brands).map(({ fields: { altText, imgSrc, link } }) => (
                <BrandItem key={altText} altText={altText} imgSrc={imgSrc} link={link} />
            ))}
        </XngCarousel>
    );
};

const BrandItem = ({ link, altText, imgSrc }: { altText: string; imgSrc: string; link?: LinkFieldValue }) => {
    const { url } = link ? useGetLink(link) : { url: "" };

    if (!url) {
        return (
            <StyledWrapper {...styles.container}>
                <LazyImage src={imgSrc} altText={altText} {...styles.img} />
            </StyledWrapper>
        );
    }

    return (
        <Clickable key={altText} href={url} {...styles.container}>
            <LazyImage src={imgSrc} altText={altText} {...styles.img} />
        </Clickable>
    );
};

const widgetModule: WidgetModule = {
    component: BrandCarouselHomePage,
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand List homepage",
        fieldDefinitions: [
            {
                name: fields.brands,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: ({ fields: { altText } }) => altText,
                fieldDefinitions: [
                    {
                        name: "imgSrc",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "altText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: false,
                    },
                ],
            },
            {
                name: fields.numberOfBrandsForDesktop,
                editorTemplate: "TextField",
                defaultValue: "6",
                fieldType: "General",
            },
            {
                name: fields.numberOfBrandsForTablet,
                editorTemplate: "TextField",
                defaultValue: "4",
                fieldType: "General",
            },
            {
                name: fields.numberOfBrandsForMobile,
                editorTemplate: "TextField",
                defaultValue: "2.5",
                fieldType: "General",
            },
            {
                name: fields.autoPlay,
                displayName: "autoplay",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
